/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ProductName } from '@pypestream/api-services';
import {
  Card,
  CardProps,
  CardWC,
  Icon,
  logos,
  TextBody,
  TextSubtitle,
} from '@pypestream/design-system';
import {
  ToolsDescriptionTranslationMapper,
  ToolsTranslationMapper,
  TranslationComponent,
  useTranslation,
} from '@pypestream/translations';
import React, { useRef, useState } from 'react';
import { debounce } from 'lodash';
import './product-cta.scss';

const autoWidthStyles = {
  flexGrow: 1,
  flexShrink: 1,
  boxSizing: 'border-box',
  maxWidth: 'clamp(7.5rem, 6.375rem + 6vw, 10.125rem)',
  minWidth: '120px',
  width: '20%',
  height: 'auto',
} as React.CSSProperties;

const topAlignedContainerStyles = {
  justifyContent: 'start',
  paddingTop: '1rem',
  paddingBottom: '1rem',
  alignSelf: 'start',
};

const autoWidthContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  alignSelf: 'center',
  flexGrow: 1,
  justifySelf: 'center',
  aspectRatio: '1/1',
} as React.CSSProperties;

export interface ProductCTAProps {
  url?: string;
  logo?: keyof typeof logos;
  name?: string;
  disabled?: boolean;
  productName?: ProductName;
  inputName?: string;
  inputValue?: string;
  checked?: boolean;
  required?: boolean;
  width?: 'auto' | 'full';
  height?: 'auto' | 'full';
  topAligned?: boolean;
  textAlign?: 'start' | 'center' | 'end' | 'justify';
  'test-id'?: string;

  onClick?: React.MouseEventHandler<CardWC>;
  onInputChange?: (isChecked: boolean) => void;
  onKeyDown?: React.KeyboardEventHandler<CardWC>;
  showLink?: boolean;
  showDeleteState?: boolean;
}

// export const NavLogoWithoutReactRouter = React.forwardRef<
//   NavLogoProps,
//   NavLogoRef
// >(({ onClick, href, children, condensed, name }, ref) => (
//   <NavLogoInner
//     href={href as string}
//     onClick={(event: React.MouseEvent<NavLogoWC, MouseEvent>) => {
//       onClick?.(event);
//     }}
//     ref={ref}
//     condensed={condensed}
//     name={name}
//   >
//     {children}
//   </NavLogoInner>
// ));

export type Ref = ProductCTAProps;

export const CardInner = React.forwardRef<
  ProductCTAProps,
  ProductCTAProps & { extraStyles?: React.CSSProperties; testId?: string }
>(
  (
    {
      url,
      logo,
      name,
      disabled,
      inputName,
      inputValue,
      productName,
      checked,
      required,
      width,
      height,
      topAligned,
      textAlign,
      testId,
      onClick,
      onInputChange,
      onKeyDown,
      extraStyles,
      showLink,
      showDeleteState,
    },
    inputRef
  ) => {
    const [t] = useTranslation();
    const [isHovering, setHovering] = useState(false);
    const [isCardStateDelayActive, setCardStateDelayActive] = useState(false);
    const [cardStateDelayTimer, setCardStateDelayTimer] = useState<
      ReturnType<typeof setTimeout> | undefined
    >(undefined);

    const cardRef = useRef<CardProps>(null);

    const focusEvent = new Event('focus', {
      bubbles: true,
    });

    const blurEvent = new Event('blur', {
      bubbles: true,
    });

    const cardTestId = logo
      ? `${logo === 'agentAssist' ? 'contact center' : logo === 'org' ? 'manager' : logo === 'proStudio' ? 'pro studio' : logo.toLowerCase()}-icon`
      : undefined;

    const imgSize = width === 'full' ? 32 : 64;

    const containerStyles =
      width === 'full'
        ? {}
        : {
            ...autoWidthContainerStyles,
            ...(topAligned ? topAlignedContainerStyles : {}),
          };

    let cardBorder: 'red' | 'primary' | 'default';

    if (checked && isHovering && !isCardStateDelayActive && showDeleteState) {
      cardBorder = 'red';
    } else if (checked || (showLink && isHovering && !disabled)) {
      cardBorder = 'primary';
    } else {
      cardBorder = 'default';
    }

    return (
      <Card
        test-id={testId}
        interactive
        ref={cardRef}
        display={width === 'full' ? 'block' : 'flex'}
        border={cardBorder}
        variant="glass"
        size="large"
        radius="large"
        shadow="none"
        style={
          width === 'full'
            ? {
                height: '100%',
              }
            : {
                ...extraStyles,
              }
        }
        {...(disabled
          ? {
              disabled,
              // @todo: change this to Tooltip component
              title: t`manager/common:products.youDontHaveAccess` || '',
              onKeyDown: undefined,
              onClick: undefined,
              href: undefined,
            }
          : {
              onKeyDown,
              onClick,
              href: url,
              disabled: false,
              title: undefined,
            })}
        onFocus={() => {
          // @ts-ignore
          inputRef?.current?.focus();
        }}
        onMouseEnter={() => {
          setHovering(true);
        }}
        onMouseLeave={() => {
          setHovering(false);
        }}
      >
        <div style={containerStyles}>
          {inputName && inputValue && !disabled && (
            <>
              <input
                ref={inputRef as React.ForwardedRef<HTMLInputElement>}
                className="c-card__input"
                type="checkbox"
                name={inputName}
                value={inputValue}
                checked={checked}
                required={required}
                onInput={(event) => {
                  if (!event.currentTarget.checked) {
                    setCardStateDelayActive(true);
                    clearTimeout(cardStateDelayTimer);
                    setCardStateDelayTimer(
                      setTimeout(() => {
                        setCardStateDelayActive(false);
                      }, 1000)
                    );
                  }
                  if (onInputChange) {
                    onInputChange(!event.currentTarget.checked);
                    event.currentTarget.blur();
                    event.currentTarget.dispatchEvent(blurEvent);
                  }
                }}
                onFocus={(event) => {
                  event.currentTarget.dispatchEvent(focusEvent);
                }}
              />
              {isHovering &&
              checked &&
              !isCardStateDelayActive &&
              showDeleteState ? (
                <span className={`c-card__icon`}>
                  <Icon name="delete" size="small" color="red" />
                </span>
              ) : (
                <span className={`c-card__checkbox`}>
                  <svg className="c-card__checkbox-svg" viewBox="0 0 12 10">
                    <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
                  </svg>
                </span>
              )}
            </>
          )}
          {!inputName && showLink && isHovering && !disabled && (
            <span className={`c-card__icon`}>
              <Icon name="open" size="small" color="purple" />
            </span>
          )}
          {logo && (
            <img
              // eslint-disable-next-line react/no-unknown-property
              test-id={cardTestId}
              src={logos[logo]}
              width={imgSize}
              height={imgSize}
              className="u-margin-bottom-small"
              alt={`${logo} icon`}
            />
          )}
          <TextSubtitle size="small" textAlign={textAlign}>
            {productName ? (
              <TranslationComponent
                i18nKey={ToolsTranslationMapper[productName]}
              >
                {name}
              </TranslationComponent>
            ) : (
              name
            )}
          </TextSubtitle>
          <div style={textAlign === 'start' ? { marginInlineEnd: -12 } : {}}>
            <TextBody size="small" variant="secondary" textAlign={textAlign}>
              <TranslationComponent
                i18nKey={
                  ToolsDescriptionTranslationMapper[productName as ProductName]
                }
              />
            </TextBody>
          </div>
        </div>
      </Card>
    );
  }
);

export const ProductCTA = React.forwardRef<ProductCTAProps, Ref>(
  (
    {
      url,
      logo,
      name,
      disabled = true,
      inputName,
      inputValue,
      productName,
      checked,
      required = false,
      width = 'auto',
      height = 'auto',
      topAligned,
      textAlign,
      'test-id': testId,
      onClick,
      onInputChange,
      onKeyDown,
      showLink,
      showDeleteState,
    },
    inputRef
  ) => {
    const sharedStyles = width === 'full' ? {} : autoWidthStyles;
    const heightStyle = height === 'full' ? '100%' : sharedStyles.height;

    return (
      <>
        {inputName && inputValue ? (
          <label className="c-product-card" style={sharedStyles}>
            <CardInner
              url={url}
              logo={logo}
              name={name}
              disabled={disabled}
              inputName={inputName}
              inputValue={inputValue}
              productName={productName}
              checked={checked}
              required={required}
              width={width}
              height={height}
              topAligned={topAligned}
              textAlign={textAlign}
              testId={testId}
              onClick={onClick}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              extraStyles={{ height: heightStyle }}
              showLink={showLink}
              showDeleteState={showDeleteState}
              ref={inputRef}
            />
          </label>
        ) : (
          <CardInner
            url={url}
            logo={logo}
            name={name}
            disabled={disabled}
            inputName={inputName}
            inputValue={inputValue}
            productName={productName}
            checked={checked}
            required={required}
            width={width}
            height={height}
            topAligned={topAligned}
            textAlign={textAlign}
            testId={testId}
            onClick={onClick}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            extraStyles={{ ...sharedStyles, height: heightStyle }}
            showLink={showLink}
            showDeleteState={showDeleteState}
            ref={inputRef}
          />
        )}
      </>
    );
  }
);
